import { htmlToSlate, slateToHtml } from '@amelio/ui-library';

import type { CommunicationEmailAudienceValue } from '@/pages/CycleEvaluation/Communications/components/CommunicationEmailCard/CommunicationEmailCard';
import type { CycleSchedule } from '@/types/cycle-evaluation/CycleSchedule';
import type { MultilingualLabel } from '@/types/Locale';
import type { EmailDefaults } from '@/utils/emailDefaults360';
import type { Descendant } from 'slate';

import { isEmptyDictionary } from '@/pages/CycleEvaluation/Communications/communicationsPersistence';
import { CommunicationEmailAudience } from '@/pages/CycleEvaluation/Communications/components/CommunicationEmailCard/CommunicationEmailCard';
import {
    isEvaluation360DefaultBody,
    isEvaluation360DefaultTitle,
    isManagerNotificationDefaultBody,
    isManagerNotificationDefaultTitle,
    isSelfEvaluationDefaultBody,
    isSelfEvaluationDefaultTitle,
} from '@/utils/emailDefaults';
import { getEmptyEmailMessage } from '@/utils/schedule';

export type CycleScheduleWithEmailSwitches = CycleSchedule & {
    isManagerEmailEnabled?: boolean;
    isEmployeeEmailEnabled?: boolean;
};

/**
 * A schedule saved before the emails became multilingual holds one bare string. Dropping it shows
 * the default template, and the next save then writes that template over the company's own text.
 */
const asMultilingualDict = (
    value: string | MultilingualLabel | undefined | null,
    plainTextLanguage: string,
): Record<string, string> | undefined => {
    if (!value) {
        return undefined;
    }

    if (typeof value === 'string') {
        return { [plainTextLanguage]: value };
    }

    return value as unknown as Record<string, string>;
};

interface LegacyScheduleFieldPair {
    title: string | MultilingualLabel | undefined;
    message: string | MultilingualLabel | undefined;
}

const legacyScheduleFieldPairFor = (
    cycleSchedule: CycleSchedule | undefined,
    audience: CommunicationEmailAudienceValue,
): LegacyScheduleFieldPair =>
    audience === CommunicationEmailAudience.EMPLOYEE
        ? { title: cycleSchedule?.employeeEmailTitle, message: cycleSchedule?.employeeEmailMessage }
        : { title: cycleSchedule?.emailTitle, message: cycleSchedule?.emailMessage };

/**
 * Absent means nobody ever chose, and the send path reads that as "send it", so only an explicit
 * `false` may switch the email off.
 */
export const resolveLegacyScheduleEmailEnabled = (
    cycleSchedule: CycleScheduleWithEmailSwitches | undefined,
    audience: CommunicationEmailAudienceValue,
): boolean =>
    (audience === CommunicationEmailAudience.EMPLOYEE
        ? cycleSchedule?.isEmployeeEmailEnabled
        : cycleSchedule?.isManagerEmailEnabled) ?? true;

export interface LegacyScheduleEmailContent {
    title: string;
    message: Descendant[];
}

/** An empty body is a body the administrator cleared on purpose, never a missing one. */
const toEmailMessage = (html: string | undefined): Descendant[] =>
    html ? htmlToSlate(html) : getEmptyEmailMessage();

/**
 * A cycle configured before an Amelio copy revision holds the OLD shipped template verbatim.
 * Reading it back as the company's own text freezes that cycle on a wording nobody chose and
 * blocks every later default revision from reaching it. The manager card and the employee card
 * are fed by different template families, so each is compared only against its own.
 */
const isShippedDefaultTitle = ({
    value,
    audience,
}: {
    value: string;
    audience: CommunicationEmailAudienceValue;
}): boolean =>
    audience === CommunicationEmailAudience.EMPLOYEE
        ? isEvaluation360DefaultTitle(value) || isSelfEvaluationDefaultTitle(value)
        : isManagerNotificationDefaultTitle(value);

const isShippedDefaultBody = ({
    value,
    audience,
}: {
    value: string;
    audience: CommunicationEmailAudienceValue;
}): boolean =>
    audience === CommunicationEmailAudience.EMPLOYEE
        ? isEvaluation360DefaultBody(value) || isSelfEvaluationDefaultBody(value)
        : isManagerNotificationDefaultBody(value);

export interface ResolveLegacyScheduleEmailParams {
    cycleSchedule: CycleSchedule | undefined;
    audience: CommunicationEmailAudienceValue;
    language: string;
    plainTextLanguage: string;
    defaults: EmailDefaults;
}

export const resolveLegacyScheduleEmailDefaults = ({
    cycleSchedule,
    audience,
    language,
    plainTextLanguage,
    defaults,
}: ResolveLegacyScheduleEmailParams): EmailDefaults => {
    const fieldPair = legacyScheduleFieldPairFor(cycleSchedule, audience);
    const titles = asMultilingualDict(fieldPair.title, plainTextLanguage);
    const bodies = asMultilingualDict(fieldPair.message, plainTextLanguage);

    const storedTitle = titles?.[language];
    const storedBody = bodies?.[language];

    // `typeof` and not `!== undefined`: `asMultilingualDict` only checked that the value is an
    // object, so this key can still come back as something other than a string.
    const isTitleCustomized =
        typeof storedTitle === 'string' && !isShippedDefaultTitle({ value: storedTitle, audience });
    const isBodyCustomized =
        typeof storedBody === 'string' && !isShippedDefaultBody({ value: storedBody, audience });

    return {
        title: isTitleCustomized ? storedTitle : defaults.title,
        body: isBodyCustomized ? storedBody : defaults.body,
    };
};

export const resolveLegacyScheduleEmailContent = (
    params: ResolveLegacyScheduleEmailParams,
): LegacyScheduleEmailContent => {
    const resolved = resolveLegacyScheduleEmailDefaults(params);

    return { title: resolved.title, message: toEmailMessage(resolved.body) };
};

export const buildLegacyScheduleEmailUpdate = ({
    cycleSchedule,
    audience,
    enabled,
    customizedLanguages,
    seedLanguage,
    titleByLanguage,
    messageByLanguage,
    plainTextLanguage,
}: {
    cycleSchedule: CycleScheduleWithEmailSwitches;
    audience: CommunicationEmailAudienceValue;
    enabled: boolean;
    customizedLanguages: ReadonlySet<string>;
    seedLanguage: string;
    titleByLanguage: Record<string, string>;
    messageByLanguage: Record<string, Descendant[]>;
    plainTextLanguage: string;
}): CycleScheduleWithEmailSwitches => {
    const fieldPair = legacyScheduleFieldPairFor(cycleSchedule, audience);
    const existingTitles = asMultilingualDict(fieldPair.title, plainTextLanguage) ?? {};
    const existingBodies = asMultilingualDict(fieldPair.message, plainTextLanguage) ?? {};

    const nextTitleByLanguage = { ...existingTitles };
    const nextMessageByLanguage = { ...existingBodies };

    for (const language of customizedLanguages) {
        if (titleByLanguage[language] !== undefined) {
            nextTitleByLanguage[language] = titleByLanguage[language];
        }
        if (messageByLanguage[language] !== undefined) {
            nextMessageByLanguage[language] = slateToHtml(messageByLanguage[language]);
        }
    }

    if (isEmptyDictionary(nextTitleByLanguage) && titleByLanguage[seedLanguage] !== undefined) {
        nextTitleByLanguage[seedLanguage] = titleByLanguage[seedLanguage];
    }

    if (isEmptyDictionary(nextMessageByLanguage) && messageByLanguage[seedLanguage] !== undefined) {
        nextMessageByLanguage[seedLanguage] = slateToHtml(messageByLanguage[seedLanguage]);
    }

    const isEmployeeAudience = audience === CommunicationEmailAudience.EMPLOYEE;

    return {
        ...cycleSchedule,
        ...(isEmployeeAudience
            ? {
                  employeeEmailTitle: nextTitleByLanguage as unknown as MultilingualLabel,
                  employeeEmailMessage: nextMessageByLanguage as unknown as MultilingualLabel,
                  isEmployeeEmailEnabled: enabled,
              }
            : {
                  emailTitle: nextTitleByLanguage as unknown as MultilingualLabel,
                  emailMessage: nextMessageByLanguage as unknown as MultilingualLabel,
                  isManagerEmailEnabled: enabled,
              }),
    };
};
